import { useCallback } from "react";
import { parseISO } from "date-fns";
import { useDispatch } from "react-redux";

import { notify } from "../../../../components/Notification";
import {
  ErrorResponse,
  getErrorMessages,
  investmentsApi,
  useCreateTransactionMutation,
  useDeleteTransactionMutation,
  useUpdateTransactionMutation,
} from "../../../../services";
import { brokerageTransferTags } from "../../../../services/api";
import { TransactionDetails } from "../../../../services/transactions/types";
import { formatDate } from "../../../../utils";
import {
  CreateTransactionsObject,
  TransactionTypeOptions,
} from "../../../Transactions";
import {
  applyPanelError,
  BrokerageTransferFormValues,
  BrokerageTransferSubmitHelpers,
  mapBrokerageTransferError,
} from "../../panels";
import { notifyLedgerCashWarnings } from "../../utils";
import {
  UseBrokerageTransferFlowProps,
  UseBrokerageTransferFlowReturn,
} from "./types";
import { useTransactionDetails } from "../../../Transactions/hooks";

// The cash that funds or drains a ledger broker — the one ledger command that
// is a normal TRANSFER transaction the API mirrors into a cash event.
export const useBrokerageTransferFlow = ({
  investmentId,
  accountId,
  investmentName,
  mode,
  transferId,
  openEditPanel,
  closePanel,
}: UseBrokerageTransferFlowProps): UseBrokerageTransferFlowReturn => {
  const dispatch = useDispatch();
  const fetchTransactionDetails = useTransactionDetails();
  const [createTransaction] = useCreateTransactionMutation();
  const [updateTransaction] = useUpdateTransactionMutation();
  const [deleteTransaction] = useDeleteTransactionMutation();

  // The generated transaction mutations cover broad finance caches. They cannot
  // know the write was also a ledger cash event, so the one complete
  // broker-specific ripple is applied here rather than left to each caller.
  const invalidateBrokerageTransfer = useCallback(() => {
    dispatch(
      investmentsApi.util.invalidateTags(
        brokerageTransferTags({ investmentId, accountId }),
      ),
    );
  }, [dispatch, investmentId, accountId]);

  const recordBrokerageTransfer = useCallback(
    async (
      values: BrokerageTransferFormValues,
      helpers: BrokerageTransferSubmitHelpers,
    ) => {
      if (!values.externalAccount) {
        helpers.setError("externalAccount", {
          type: "required",
          message: "External account is required!",
        });
        return;
      }

      const brokerageAccount = {
        label: investmentName,
        value: String(accountId),
      };
      // Narrowed once, here: below, a non-null `editedTransferId` *is* the edit
      // branch, so neither the write nor the toast has to re-test the mode.
      const editedTransferId = mode === "edit" ? transferId : null;
      // An invariant backstop, not a reachable branch: `openEditPanel` always sets
      // the id alongside the mode. It stays because a null id here would silently
      // create a *second* transfer instead of correcting the one on screen.
      if (mode === "edit" && !editedTransferId) {
        helpers.setPanelError("This cash transfer is no longer available.");
        return;
      }
      const isDeposit = values.direction === "DEPOSIT";
      const payload: CreateTransactionsObject = {
        fromAccount: isDeposit ? values.externalAccount : brokerageAccount,
        toAccount: isDeposit ? brokerageAccount : values.externalAccount,
        amount: String(values.amountEur),
        transactionType: {
          label: TransactionTypeOptions.TRANSFER,
          value: TransactionTypeOptions.TRANSFER,
        },
        transactionDate: formatDate(values.date),
        note: values.note,
        category: null,
        subCategory: null,
        isRecurring: false,
        frequency: null,
      };

      try {
        if (editedTransferId) {
          await updateTransaction({
            transactionId: editedTransferId,
            transaction: payload,
          }).unwrap();
          notify.success("Cash transfer updated");
          // No `notifyLedgerCashWarnings` here, and that is deliberate rather
          // than an omission: `PUT /transactions/:id` answers `void`, because the
          // API drops the re-created leg's point-in-time D8 warning
          // (`transactionsService.ts`). There is nothing to report.
        } else {
          const result = await createTransaction(payload).unwrap();
          notify.success(isDeposit ? "Cash deposited" : "Cash withdrawn");
          // After the success toast, exactly as on the trade and account-cash
          // paths: a withdrawal is a debit on the broker's own cash, so it can
          // leave the account short at the movement's date. The movement was
          // still recorded — the warning says what the balance now is, it does
          // not retract the success above.
          notifyLedgerCashWarnings(result);
        }
        invalidateBrokerageTransfer();
        closePanel();
      } catch (err: unknown) {
        // The global middleware raises the toast. Keep this panel open and add a
        // local, actionable destination for the exact server validation.
        applyPanelError(
          mapBrokerageTransferError(getErrorMessages(err as ErrorResponse)),
          helpers,
        );
      }
    },
    [
      accountId,
      investmentName,
      mode,
      transferId,
      createTransaction,
      updateTransaction,
      invalidateBrokerageTransfer,
      closePanel,
    ],
  );

  /**
   * The server owns the editable classification; this fetch fills in the note
   * and verifies the brokerage endpoint before the row reaches the panel. The
   * endpoint-derived direction is intentionally pinned here: swapping these
   * sides would reverse a real transfer when `PUT /transactions/:id` recreates
   * it.
   */
  const openBrokerageTransferEdit = useCallback(
    async (transactionId: number) => {
      let details: TransactionDetails;
      try {
        details = await fetchTransactionDetails(transactionId);
      } catch (err: unknown) {
        // Only the rejected request goes through the API-error path; a locally
        // detected problem is raised below with its own sentence, because
        // `notify.apiError` reads an `errors` field a plain `Error` never has and
        // would swallow it into the generic server-error toast.
        notify.apiError(err as ErrorResponse);
        return;
      }

      const isDeposit = details.toAccountId === accountId;
      const isWithdrawal = details.fromAccountId === accountId;
      let externalAccountId: number | null = null;
      if (isDeposit) {
        externalAccountId = details.fromAccountId;
      } else if (isWithdrawal) {
        externalAccountId = details.toAccountId;
      }
      if (!externalAccountId) {
        notify.error(
          "This transfer no longer belongs to this brokerage account.",
        );
        return;
      }
      openEditPanel(transactionId, {
        transferId: transactionId,
        direction: isDeposit ? "DEPOSIT" : "WITHDRAWAL",
        amountEur: Math.abs(details.amount),
        // `parseISO`, not `new Date`: a date-only ISO string parses as UTC
        // midnight, which renders as the previous day west of Greenwich — and
        // `formatDate(values.date)` is what an untouched save sends back.
        date: parseISO(details.transactionDate),
        externalAccountId,
        note: details.note ?? "",
      });
    },
    [accountId, fetchTransactionDetails, openEditPanel],
  );

  /**
   * Deletes the transaction behind an Activity transfer row.
   *
   * Rethrown rather than swallowed: the feed keeps its confirmation open on a
   * rejection so the reason stays beside the button that would retry it.
   */
  const deleteBrokerageTransfer = useCallback(
    async (transactionId: number) => {
      await deleteTransaction(transactionId).unwrap();
      notify.success("Cash transfer deleted");
      invalidateBrokerageTransfer();
    },
    [deleteTransaction, invalidateBrokerageTransfer],
  );

  return {
    recordBrokerageTransfer,
    openBrokerageTransferEdit,
    deleteBrokerageTransfer,
  };
};
